import React, { useState, useEffect, useCallback, useContext } from 'react';
import api from '../../api';
import { AuthContext } from '../../context/AuthContext';
import { formatDateTime } from '../../utils/timeUtils';
import {
    getWeeklyRecapPrompts,
    getWeeklyRecapPrompt,
    updateWeeklyRecapPrompt,
    updateWeeklyRecapResponse,
    generateWeeklyRecapPrompt,
    sendPromptToLLM,
    getVerifiedAdmins,
    testPromptNotification,
    getNextGenerationTime,
    approvePrompt,
    approveRecap,
    deletePromptPackage,
    sendRecapNow,
    updateScheduling
} from '../../adminApi';
import WeeklyRecapWorkflow from './WeeklyRecapWorkflow';
import styles from './LLMAdminPanel.module.css';

const LLMAdminPanel = () => {
    const { user } = useContext(AuthContext);
    const [activeTab, setActiveTab] = useState('settings');
    const [settings, setSettings] = useState({});
    const [usageStats, setUsageStats] = useState(null);
    const [usageHistory, setUsageHistory] = useState([]);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [testResult, setTestResult] = useState(null);
    const [testLoading, setTestLoading] = useState(false);
    const [groundRules, setGroundRules] = useState('');
    const [groundRulesLoading, setGroundRulesLoading] = useState(false);
    const [availableModels, setAvailableModels] = useState([]);
    const [modelsLoading, setModelsLoading] = useState(false);

    // Weekly Recap Prompt state
    const [weeklyRecapPrompts, setWeeklyRecapPrompts] = useState([]);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [promptLoading, setPromptLoading] = useState(false);
    const [promptError, setPromptError] = useState(null);
    const [promptSuccess, setPromptSuccess] = useState(null);
    const [editingPrompt, setEditingPrompt] = useState(false);
    const [editedPromptPackage, setEditedPromptPackage] = useState(null);
    const [llmResponse, setLlmResponse] = useState(null);
    const [llmLoading, setLlmLoading] = useState(false);
    const [verifiedAdmins, setVerifiedAdmins] = useState([]);
    const [nextGenerationTime, setNextGenerationTime] = useState(null);
    const [editingRecap, setEditingRecap] = useState(false);
    const [editedRecap, setEditedRecap] = useState('');

    // Test data templates - only for features that can be tested independently
    const testTemplates = {
        milestone: {
            milestoneType: 'Hot Streak',
            username: 'TestUser',
            details: '5 game winning streak'
        },
        closeLockAlert: {
            username: 'TestUser',
            pickDisplay: 'Over 45.5',
            situation: 'needs 3 more points in the final 2 minutes',
            score: 'LSU 21 - Alabama 18',
            gameDisplay: 'LSU @ Alabama',
            clock: '1:47',
            dramaText: 'is LOSING by 3 - needs more points!'
        }
    };

    const loadAvailableModels = useCallback(async () => {
        setModelsLoading(true);
        try {
            const response = await api.getAvailableModels();
            if (response.data.success) {
                setAvailableModels(response.data.models);
            } else {
                console.error('Failed to load available models:', response.data.error);
                setAvailableModels([]);
            }
        } catch (error) {
            console.error('Error loading available models:', error);
            setAvailableModels([]);
        } finally {
            setModelsLoading(false);
        }
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            // Load settings
            const settingsResponse = await api.get('/admin/llm/settings');
            setSettings(settingsResponse.data);

            // Load status
            const statusResponse = await api.get('/admin/llm/status');
            setStatus(statusResponse.data);

            // Load usage stats
            const statsResponse = await api.get('/admin/llm/usage-stats?days=30');
            setUsageStats(statsResponse.data);

            // Load usage history if on history tab
            if (activeTab === 'history') {
                const historyResponse = await api.get('/admin/llm/usage-history?limit=50');
                setUsageHistory(historyResponse.data.history);
            }

            // Load ground rules if on ground rules tab
            if (activeTab === 'groundRules') {
                const groundRulesResponse = await api.get('/admin/llm/ground-rules');
                setGroundRules(groundRulesResponse.data.rules);
            }

            // Load available models if on settings tab
            if (activeTab === 'settings') {
                await loadAvailableModels();
            }

            // Load weekly recap prompts if on weekly recap tab
            if (activeTab === 'weeklyRecap') {
                const promptsResponse = await getWeeklyRecapPrompts();
                setWeeklyRecapPrompts(promptsResponse.data.prompts || []);

                const adminsResponse = await getVerifiedAdmins();
                setVerifiedAdmins(adminsResponse.data.adminUsers || []);

                // Load next generation time
                await loadNextGenerationTime();
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load LLM data');
        } finally {
            setLoading(false);
        }
    }, [activeTab, loadAvailableModels]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const updateSettings = async (key, value) => {
        try {
            await api.put('/admin/llm/settings', {
                settings: { [key]: value }
            });

            setSettings(prev => ({
                ...prev,
                [key]: { ...prev[key], value }
            }));

            setSuccess(`Updated ${key} successfully`);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update setting');
        }
    };

    const testLLMGeneration = async (messageType) => {
        setTestLoading(true);
        setTestResult(null);
        setError(null);

        try {
            const response = await api.post('/admin/llm/test-generation', {
                messageType,
                testData: testTemplates[messageType]
            });

            setTestResult(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to test LLM generation');
        } finally {
            setTestLoading(false);
        }
    };

    const updateGroundRules = async () => {
        setGroundRulesLoading(true);
        setError(null);

        try {
            await api.put('/admin/llm/ground-rules', {
                rules: groundRules
            });

            setSuccess('Ground rules updated successfully');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update ground rules');
        } finally {
            setGroundRulesLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 4
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const renderSettings = () => (
        <div className={styles.settingsContainer}>
            <h3>🤖 LLM Configuration</h3>

            {status && (
                <div className={styles.statusCard}>
                    <h4>Service Status</h4>
                    <div className={styles.statusGrid}>
                        <div className={styles.statusItem}>
                            <span className={styles.statusLabel}>API Key:</span>
                            <span className={status.hasApiKey ? styles.statusGood : styles.statusBad}>
                                {status.hasApiKey ? '✅ Configured' : '❌ Missing'}
                            </span>
                        </div>
                        <div className={styles.statusItem}>
                            <span className={styles.statusLabel}>LLM Service:</span>
                            <label className={styles.switch}>
                                <input
                                    type="checkbox"
                                    checked={settings.llm_enabled?.value === 'true'}
                                    onChange={(e) => updateSettings('llm_enabled', e.target.checked.toString())}
                                    aria-label="Toggle LLM Service"
                                />
                                <span className={styles.slider}></span>
                            </label>
                        </div>
                        <div className={styles.statusItem}>
                            <span className={styles.statusLabel}>Model:</span>
                            <span>{status.model || settings.llm_model?.value || 'Not Set'}</span>
                        </div>
                        <div className={styles.statusItem}>
                            <span className={styles.statusLabel}>Max Tokens:</span>
                            <span>{status.maxTokens || settings.llm_max_tokens?.value || 'Not Set'}</span>
                        </div>
                        <div className={styles.statusItem}>
                            <span className={styles.statusLabel}>Temperature:</span>
                            <span>{status.temperature || settings.llm_temperature?.value || 'Not Set'}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Configuration Settings Section */}
            <div className={styles.configSection}>
                <h4>⚙️ Configuration Settings</h4>
                <div className={styles.configGrid}>
                    <div className={styles.settingItem}>
                        <label className={styles.settingLabel}>Default Model</label>
                        {modelsLoading ? (
                            <div className={styles.loadingIndicator}>Loading models...</div>
                        ) : (
                            <select
                                value={settings.llm_model?.value || ''}
                                onChange={(e) => updateSettings('llm_model', e.target.value)}
                                className={styles.selectInput}
                            >
                                <option value="">Select a model...</option>
                                {availableModels.map(model => (
                                    <option key={model.id} value={model.id}>
                                        {model.name}
                                        {model.id.includes('gpt-5') && ' 🌟 (Recommended)'}
                                        {model.id.includes('gpt-4o-mini') && ' 💰 (Cost Effective)'}
                                    </option>
                                ))}
                            </select>
                        )}
                        <div className={styles.helperText}>
                            Choose from your available OpenAI models. GPT-5 models provide better quality for weekly recaps.
                        </div>
                    </div>
                    <div className={styles.settingItem}>
                        <label className={styles.settingLabel}>Max Tokens per Response</label>
                        <input
                            type="number"
                            value={settings.llm_max_tokens?.value || ''}
                            onChange={(e) => updateSettings('llm_max_tokens', e.target.value)}
                            className={styles.numberInput}
                            min="1"
                            max="4000"
                        />
                    </div>
                    <div className={styles.settingItem}>
                        <label className={styles.settingLabel}>Creativity Level (0.0-1.0)</label>
                        <input
                            type="number"
                            value={settings.llm_temperature?.value || ''}
                            onChange={(e) => updateSettings('llm_temperature', e.target.value)}
                            className={styles.numberInput}
                            step="0.1"
                            min="0"
                            max="1"
                        />
                    </div>
                    <div className={styles.settingItem}>
                        <label className={styles.settingLabel}>Max Requests per Day</label>
                        <input
                            type="number"
                            value={settings.llm_daily_limit?.value || ''}
                            onChange={(e) => updateSettings('llm_daily_limit', e.target.value)}
                            className={styles.numberInput}
                            min="1"
                        />
                    </div>
                    <div className={styles.settingItem}>
                        <label className={styles.settingLabel}>Max Monthly Spending ($)</label>
                        <input
                            type="number"
                            value={settings.llm_monthly_budget?.value || ''}
                            onChange={(e) => updateSettings('llm_monthly_budget', e.target.value)}
                            className={styles.numberInput}
                            step="0.01"
                            min="0"
                        />
                    </div>
                </div>
            </div>

            {/* Feature Toggles Section */}
            <div className={styles.featuresSection}>
                <h4>🎛️ Feature Toggles</h4>
                <div className={styles.featuresGrid}>
                    <div className={styles.settingItem}>
                        <label className={styles.settingLabel}>Enable LLM for Close Lock Alerts</label>
                        <label className={styles.switch}>
                            <input
                                type="checkbox"
                                checked={settings.llm_close_alerts_enabled?.value === 'true'}
                                onChange={(e) => updateSettings('llm_close_alerts_enabled', e.target.checked.toString())}
                                aria-label="Toggle LLM Close Lock Alerts"
                            />
                            <span className={styles.slider}></span>
                        </label>
                        <div className={styles.settingDescription}>
                            Use LLM for dynamic close lock alerts, otherwise use enhanced programmatic alerts
                        </div>
                    </div>
                    <div className={styles.settingItem}>
                        <label className={styles.settingLabel}>Enable LLM for Milestone Messages</label>
                        <label className={styles.switch}>
                            <input
                                type="checkbox"
                                checked={settings.llm_milestone_enabled?.value === 'true'}
                                onChange={(e) => updateSettings('llm_milestone_enabled', e.target.checked.toString())}
                                aria-label="Toggle LLM Milestone Messages"
                            />
                            <span className={styles.slider}></span>
                        </label>
                        <div className={styles.settingDescription}>
                            Use LLM for individual milestone callouts (win streaks, losing streaks, bounce backs)
                        </div>
                    </div>
                    <div className={styles.settingItem}>
                        <label className={styles.settingLabel}>Enable LLM for Weekly Recaps</label>
                        <label className={styles.switch}>
                            <input
                                type="checkbox"
                                checked={settings.llm_weekly_recap_enabled?.value === 'true'}
                                onChange={(e) => updateSettings('llm_weekly_recap_enabled', e.target.checked.toString())}
                                aria-label="Toggle LLM Weekly Recaps"
                            />
                            <span className={styles.slider}></span>
                        </label>
                        <div className={styles.settingDescription}>
                            Use LLM for weekly recap generation, otherwise use programmatic recap
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderUsageStats = () => (
        <div className={styles.statsContainer}>
            <h3>📊 Usage Statistics (Last 30 Days)</h3>

            {usageStats && (
                <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                        <h4>Total Requests</h4>
                        <div className={styles.statValue}>{usageStats.total_requests || 0}</div>
                    </div>
                    <div className={styles.statCard}>
                        <h4>Total Cost</h4>
                        <div className={styles.statValue}>{formatCurrency(usageStats.total_cost || 0)}</div>
                    </div>
                    <div className={styles.statCard}>
                        <h4>Avg Cost/Request</h4>
                        <div className={styles.statValue}>{formatCurrency(usageStats.avg_cost_per_request || 0)}</div>
                    </div>
                    <div className={styles.statCard}>
                        <h4>Total Tokens</h4>
                        <div className={styles.statValue}>{usageStats.total_tokens || 0}</div>
                    </div>
                </div>
            )}

            {usageStats?.byType && (
                <div className={styles.usageByType}>
                    <h4>Usage by Message Type</h4>
                    <div className={styles.typeGrid}>
                        {usageStats.byType.map((type) => (
                            <div key={type.message_type} className={styles.typeCard}>
                                <div className={styles.typeName}>{type.message_type}</div>
                                <div className={styles.typeStats}>
                                    <span>{type.count} requests</span>
                                    <span>{formatCurrency(type.cost)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {status?.recentUsage && (
                <div className={styles.recentUsage}>
                    <h4>Last 24 Hours</h4>
                    <div className={styles.recentStats}>
                        <div className={styles.recentItem}>
                            <span>Requests:</span>
                            <span>{status.recentUsage.requests_24h}</span>
                        </div>
                        <div className={styles.recentItem}>
                            <span>Cost:</span>
                            <span>{formatCurrency(status.recentUsage.cost_24h)}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    const renderUsageHistory = () => (
        <div className={styles.historyContainer}>
            <h3>📋 Usage History</h3>

            <div className={styles.historyTable}>
                <div className={styles.historyHeader}>
                    <div>Date</div>
                    <div>Type</div>
                    <div>Tokens</div>
                    <div>Cost</div>
                    <div>Model</div>
                </div>
                {usageHistory.map((entry) => (
                    <div key={`${entry.id}-${entry.created_at}`} className={styles.historyRow}>
                        <div>{formatDate(entry.created_at)}</div>
                        <div className={styles.messageType}>{entry.message_type}</div>
                        <div>{entry.total_tokens}</div>
                        <div>{formatCurrency(entry.cost)}</div>
                        <div>{entry.model}</div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderTestInterface = () => (
        <div className={styles.testContainer}>
            <h3>🧪 Test LLM vs Programmatic Generation</h3>
            <p className={styles.testDescription}>
                Compare LLM-generated content with programmatic fallbacks for individual features.
                Weekly recap tests are available on the Weekly Recap tab.
            </p>

            <div className={styles.testGrid}>
                {Object.entries(testTemplates).map(([type, template]) => (
                    <div key={type} className={styles.testCard}>
                        <h4>{type.charAt(0).toUpperCase() + type.slice(1).replace(/([A-Z])/g, ' $1')} Test</h4>
                        <div className={styles.testData}>
                            <pre>{JSON.stringify(template, null, 2)}</pre>
                        </div>
                        <button
                            onClick={() => testLLMGeneration(type)}
                            disabled={testLoading}
                            className={styles.testButton}
                        >
                            {testLoading ? 'Testing...' : 'Compare LLM vs Programmatic'}
                        </button>
                    </div>
                ))}
            </div>

            {testResult && (
                <div className={styles.testResult}>
                    <h4>Test Results - {testResult.messageType.charAt(0).toUpperCase() + testResult.messageType.slice(1).replace(/([A-Z])/g, ' $1')}</h4>
                    <div className={styles.resultContent}>
                        <div className={styles.resultMeta}>
                            <span>Type: {testResult.messageType}</span>
                            <span>Response Time: {testResult.responseTime}ms</span>
                        </div>

                        {testResult.llmContent && (
                            <div className={styles.comparisonSection}>
                                <h5>🤖 LLM Generated Content:</h5>
                                <div className={styles.contentText}>{testResult.llmContent}</div>
                            </div>
                        )}

                        {testResult.programmaticContent && (
                            <div className={styles.comparisonSection}>
                                <h5>⚙️ Programmatic Fallback:</h5>
                                <div className={styles.contentText}>{testResult.programmaticContent}</div>
                            </div>
                        )}

                        {testResult.generatedContent && !testResult.llmContent && !testResult.programmaticContent && (
                            <div className={styles.comparisonSection}>
                                <h5>📝 Generated Content:</h5>
                                <div className={styles.contentText}>{testResult.generatedContent}</div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    const renderGroundRules = () => (
        <div className={styles.groundRulesContainer}>
            <h3>📋 Ground Rules & Preferences</h3>
            <p className={styles.groundRulesDescription}>
                Set the personality, tone, and style guidelines that the LLM will follow for all generated content.
                These rules help maintain consistency and ensure the AI matches your league's voice.
            </p>

            <div className={styles.groundRulesEditor}>
                <label htmlFor="groundRulesTextarea" className={styles.groundRulesLabel}>
                    LLM Ground Rules & Style Guidelines:
                </label>
                <textarea
                    id="groundRulesTextarea"
                    value={groundRules}
                    onChange={(e) => setGroundRules(e.target.value)}
                    className={styles.groundRulesTextarea}
                    placeholder="Enter your ground rules and style preferences here..."
                    rows={15}
                />
                <div className={styles.groundRulesActions}>
                    <button
                        onClick={updateGroundRules}
                        disabled={groundRulesLoading}
                        className={styles.updateButton}
                    >
                        {groundRulesLoading ? 'Updating...' : 'Update Ground Rules'}
                    </button>
                    <button
                        onClick={() => setGroundRules('')}
                        className={styles.clearButton}
                    >
                        Clear
                    </button>
                </div>
            </div>

            <div className={styles.groundRulesInfo}>
                <h4>💡 Tips for Effective Ground Rules:</h4>
                <ul>
                    <li>Define the personality and tone you want (enthusiastic, professional, casual, etc.)</li>
                    <li>Set character limits and formatting preferences</li>
                    <li>Specify emoji usage and frequency</li>
                    <li>Include examples of good and bad messages</li>
                    <li>Set guidelines for different message types (milestones, drama, alerts)</li>
                    <li>Define how to handle different situations (wins, losses, close games)</li>
                </ul>
            </div>
        </div>
    );

    // Weekly Recap Prompt Functions
    const loadWeeklyRecapPrompt = async (weekNumber, season) => {
        setPromptLoading(true);
        setPromptError(null);
        setPromptSuccess(null);

        try {
            const response = await getWeeklyRecapPrompt(weekNumber, season);
            setSelectedPrompt(response.data.promptPackage);
            setEditedPromptPackage(response.data.promptPackage);
            setEditingPrompt(false);
            setLlmResponse(response.data.promptPackage.llmResponse || null);
            setEditedRecap(response.data.promptPackage.llmResponse || '');
            setEditingRecap(false);
        } catch (error) {
            setPromptError(error.response?.data?.error || 'Failed to load prompt package');
        } finally {
            setPromptLoading(false);
        }
    };

    const generateNewPrompt = async (weekNumber, season) => {
        setPromptLoading(true);
        setPromptError(null);
        setPromptSuccess(null);

        try {
            const response = await generateWeeklyRecapPrompt(weekNumber, season);
            setSelectedPrompt(response.data.promptPackage);
            setEditedPromptPackage(response.data.promptPackage);
            setEditingPrompt(false);
            setLlmResponse(null);
            setPromptSuccess('Prompt package generated successfully!');

            // Refresh the prompts list
            const promptsResponse = await getWeeklyRecapPrompts();
            setWeeklyRecapPrompts(promptsResponse.data.prompts || []);
        } catch (error) {
            setPromptError(error.response?.data?.error || 'Failed to generate prompt package');
        } finally {
            setPromptLoading(false);
        }
    };

    const savePromptEdits = async () => {
        if (!selectedPrompt || !editedPromptPackage) return;

        setPromptLoading(true);
        setPromptError(null);
        setPromptSuccess(null);

        try {
            await updateWeeklyRecapPrompt(
                selectedPrompt.weekNumber,
                selectedPrompt.season,
                editedPromptPackage,
                'edited'
            );
            setSelectedPrompt(editedPromptPackage);
            setEditingPrompt(false);
            setPromptSuccess('Prompt package updated successfully!');
        } catch (error) {
            setPromptError(error.response?.data?.error || 'Failed to update prompt package');
        } finally {
            setPromptLoading(false);
        }
    };

    const sendToLLM = async () => {
        if (!selectedPrompt) return;

        setLlmLoading(true);
        setPromptError(null);
        setPromptSuccess(null);

        try {
            const response = await sendPromptToLLM(
                selectedPrompt.weekNumber,
                selectedPrompt.season,
                selectedPrompt
            );
            setLlmResponse(response.data.llmResponse);
            setEditedRecap(response.data.llmResponse);
            setPromptSuccess('LLM response generated and stored successfully!');
            // Refresh the prompt list to show updated status
            const promptsResponse = await getWeeklyRecapPrompts();
            setWeeklyRecapPrompts(promptsResponse.data.prompts || []);
        } catch (error) {
            setPromptError(error.response?.data?.error || 'Failed to send prompt to LLM');
        } finally {
            setLlmLoading(false);
        }
    };

    const testDiscordNotification = async (weekNumber, season) => {
        try {
            const response = await testPromptNotification(weekNumber, season);
            setPromptSuccess(`Test notification sent to ${response.data.results.notified} admins`);
        } catch (error) {
            setPromptError(error.response?.data?.error || 'Failed to send test notification');
        }
    };

    // Enhanced Weekly Recap Functions
    const loadNextGenerationTime = async () => {
        try {
            const response = await getNextGenerationTime();
            setNextGenerationTime(response.data.nextGenerationTime);
        } catch (error) {
            console.error('Failed to load next generation time:', error);
        }
    };

    const handleApprovePrompt = async (weekNumber, season) => {
        try {
            setPromptLoading(true);
            const response = await approvePrompt(weekNumber, season);
            if (response.data.success) {
                setPromptSuccess('Prompt approved successfully');
                // Refresh the prompts list
                const promptsResponse = await getWeeklyRecapPrompts();
                setWeeklyRecapPrompts(promptsResponse.data.prompts || []);
            } else {
                setPromptError('Failed to approve prompt');
            }
        } catch (error) {
            setPromptError(error.response?.data?.error || 'Failed to approve prompt');
        } finally {
            setPromptLoading(false);
        }
    };

    const handleApproveRecap = async (weekNumber, season) => {
        try {
            setPromptLoading(true);
            const response = await approveRecap(weekNumber, season);
            if (response.data.success) {
                setPromptSuccess('Recap approved successfully');
                // Refresh the prompts list
                const promptsResponse = await getWeeklyRecapPrompts();
                setWeeklyRecapPrompts(promptsResponse.data.prompts || []);
            } else {
                setPromptError('Failed to approve recap');
            }
        } catch (error) {
            setPromptError(error.response?.data?.error || 'Failed to approve recap');
        } finally {
            setPromptLoading(false);
        }
    };

    const handleDeletePromptPackage = async (weekNumber, season) => {
        if (!window.confirm(`Are you sure you want to delete the prompt package for Week ${weekNumber}, Season ${season}?`)) {
            return;
        }

        try {
            setPromptLoading(true);
            const response = await deletePromptPackage(weekNumber, season);
            if (response.data.success) {
                setPromptSuccess('Prompt package deleted successfully');
                // Refresh the prompts list
                const promptsResponse = await getWeeklyRecapPrompts();
                setWeeklyRecapPrompts(promptsResponse.data.prompts || []);
                setSelectedPrompt(null); // Clear selection
            } else {
                setPromptError('Failed to delete prompt package');
            }
        } catch (error) {
            setPromptError(error.response?.data?.error || 'Failed to delete prompt package');
        } finally {
            setPromptLoading(false);
        }
    };

    const handleSendRecapNow = async (weekNumber, season) => {
        if (!window.confirm(`Are you sure you want to send the recap for Week ${weekNumber}, Season ${season} immediately? This will cancel any scheduled send time.`)) {
            return;
        }

        try {
            setPromptLoading(true);
            const response = await sendRecapNow(weekNumber, season);
            if (response.data.success) {
                setPromptSuccess('Recap sent successfully');
                // Refresh the prompts list
                const promptsResponse = await getWeeklyRecapPrompts();
                setWeeklyRecapPrompts(promptsResponse.data.prompts || []);
            } else {
                setPromptError('Failed to send recap');
            }
        } catch (error) {
            setPromptError(error.response?.data?.error || 'Failed to send recap');
        } finally {
            setPromptLoading(false);
        }
    };

    const handleUpdateScheduling = async (weekNumber, season, scheduledTime, cronJobKey) => {
        try {
            setPromptLoading(true);
            const response = await updateScheduling(weekNumber, season, scheduledTime, cronJobKey);
            if (response.data.success) {
                setPromptSuccess('Scheduling updated successfully');
                // Refresh the prompts list
                const promptsResponse = await getWeeklyRecapPrompts();
                setWeeklyRecapPrompts(promptsResponse.data.prompts || []);
            } else {
                setPromptError('Failed to update scheduling');
            }
        } catch (error) {
            setPromptError(error.response?.data?.error || 'Failed to update scheduling');
        } finally {
            setPromptLoading(false);
        }
    };

    const saveRecapEdits = async () => {
        if (!selectedPrompt || !editedRecap) return;

        try {
            setPromptLoading(true);
            // Update the LLM response in the database
            const response = await updateWeeklyRecapResponse(
                selectedPrompt.weekNumber,
                selectedPrompt.season,
                editedRecap
            );

            if (response.data.success) {
                setPromptSuccess('Recap edits saved successfully');
                setEditingRecap(false);
                // Refresh the prompts list
                const promptsResponse = await getWeeklyRecapPrompts();
                setWeeklyRecapPrompts(promptsResponse.data.prompts || []);
                // Reload the selected prompt to get updated data
                loadWeeklyRecapPrompt(selectedPrompt.weekNumber, selectedPrompt.season);
            } else {
                setPromptError('Failed to save recap edits');
            }
        } catch (error) {
            setPromptError(error.response?.data?.error || 'Failed to save recap edits');
        } finally {
            setPromptLoading(false);
        }
    };

    const renderWeeklyRecapTab = () => {
        return (
            <div className={styles.weeklyRecapContainer}>
                <div className={styles.section}>
                    <h3>📊 Weekly Recap Prompt Management</h3>
                    <p>Manage weekly recap prompt packages and LLM generation</p>
                </div>

                {promptError && (
                    <div className={styles.errorMessage}>
                        ❌ {promptError}
                    </div>
                )}

                {promptSuccess && (
                    <div className={styles.successMessage}>
                        ✅ {promptSuccess}
                    </div>
                )}

                {/* Admin Discord Status */}
                <div className={styles.section}>
                    <h4>📢 Admin Discord Notifications</h4>
                    <p>Verified admins with Discord accounts: <strong>{verifiedAdmins.length}</strong></p>
                    {verifiedAdmins.length > 0 && (
                        <div className={styles.adminList}>
                            {verifiedAdmins.map((admin) => (
                                <span key={`${admin.username}-${admin.discord_username}`} className={styles.adminTag}>
                                    {admin.username} ({admin.discord_username})
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Next Generation Time */}
                {nextGenerationTime && nextGenerationTime !== '00' && nextGenerationTime !== '0' && (
                    <div className={styles.section}>
                        <h4>⏰ Next Anticipated Prompt Generation</h4>
                        <p>
                            <strong>Estimated Time:</strong> {new Date(nextGenerationTime).toLocaleString()}
                            <br />
                            <small>Based on last game with picks + 3 hours</small>
                        </p>
                    </div>
                )}

                {/* Stored Prompts List */}
                <div className={styles.section}>
                    <h4>📋 Stored Prompt Packages</h4>
                    {weeklyRecapPrompts.length === 0 ? (
                        <p>No prompt packages found. Generate one for a completed week.</p>
                    ) : (
                        <div className={styles.promptsList}>
                            {weeklyRecapPrompts.map((prompt) => (
                                <div key={`${prompt.weekNumber}-${prompt.season}`} className={styles.promptItem}>
                                    <div className={styles.promptInfo}>
                                        <strong>Week {prompt.weekNumber}, {prompt.season}</strong>
                                        <small>Created: {formatDateTime(prompt.createdAt, user)}</small>

                                        {/* Workflow Status Display */}
                                        <WeeklyRecapWorkflow
                                            prompt={prompt}
                                            onUpdateScheduling={handleUpdateScheduling}
                                        />
                                    </div>
                                    <div className={styles.promptActions}>
                                        <button
                                            onClick={() => loadWeeklyRecapPrompt(prompt.weekNumber, prompt.season)}
                                            className={styles.actionButton}
                                            disabled={prompt.status === 'pending_generation'}
                                        >
                                            Load
                                        </button>
                                        <button
                                            onClick={() => generateNewPrompt(prompt.weekNumber, prompt.season)}
                                            className={styles.actionButton}
                                        >
                                            {prompt.status === 'pending_generation' ? 'Force Generate' : 'Regenerate'}
                                        </button>
                                        <button
                                            onClick={() => testDiscordNotification(prompt.weekNumber, prompt.season)}
                                            className={styles.actionButton}
                                        >
                                            Test Notify
                                        </button>
                                        <button
                                            onClick={() => handleDeletePromptPackage(prompt.weekNumber, prompt.season)}
                                            className={styles.actionButton}
                                            style={{ backgroundColor: '#dc3545' }}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Selected Prompt Package */}
                {selectedPrompt && (
                    <div className={styles.section}>
                        <h4>📝 Prompt Package - Week {selectedPrompt.weekNumber}, {selectedPrompt.season}</h4>

                        <div className={styles.promptControls}>
                            <button
                                onClick={() => setEditingPrompt(!editingPrompt)}
                                className={styles.actionButton}
                            >
                                {editingPrompt ? 'Cancel Edit' : 'Edit Prompt'}
                            </button>
                            {editingPrompt && (
                                <button
                                    onClick={savePromptEdits}
                                    disabled={promptLoading}
                                    className={styles.actionButton}
                                >
                                    {promptLoading ? 'Saving...' : 'Save Changes'}
                                </button>
                            )}
                            <button
                                onClick={sendToLLM}
                                disabled={llmLoading}
                                className={styles.actionButton}
                            >
                                {llmLoading ? 'Generating...' : 'Send to LLM'}
                            </button>

                            {/* Approval Buttons */}
                            {!selectedPrompt.promptApproved && (
                                <button
                                    onClick={() => handleApprovePrompt(selectedPrompt.weekNumber, selectedPrompt.season)}
                                    disabled={promptLoading}
                                    className={styles.actionButton}
                                    style={{ backgroundColor: '#28a745' }}
                                >
                                    Approve Prompt
                                </button>
                            )}

                            {selectedPrompt.llmResponse && !selectedPrompt.recapApproved && (
                                <button
                                    onClick={() => handleApproveRecap(selectedPrompt.weekNumber, selectedPrompt.season)}
                                    disabled={promptLoading}
                                    className={styles.actionButton}
                                    style={{ backgroundColor: '#28a745' }}
                                >
                                    Approve Recap
                                </button>
                            )}

                            {/* Send Now Button */}
                            {selectedPrompt.llmResponse && (
                                <button
                                    onClick={() => handleSendRecapNow(selectedPrompt.weekNumber, selectedPrompt.season)}
                                    disabled={promptLoading}
                                    className={styles.actionButton}
                                    style={{ backgroundColor: '#007bff' }}
                                >
                                    Send Recap Now
                                </button>
                            )}
                        </div>

                        {/* System Prompt */}
                        <div className={styles.promptSection}>
                            <h5>System Prompt:</h5>
                            {editingPrompt ? (
                                <textarea
                                    value={editedPromptPackage.systemPrompt}
                                    onChange={(e) => setEditedPromptPackage({
                                        ...editedPromptPackage,
                                        systemPrompt: e.target.value
                                    })}
                                    className={styles.promptTextarea}
                                    rows={8}
                                />
                            ) : (
                                <pre className={styles.promptDisplay}>{selectedPrompt.systemPrompt}</pre>
                            )}
                        </div>

                        {/* User Prompt */}
                        <div className={styles.promptSection}>
                            <h5>User Prompt:</h5>
                            {editingPrompt ? (
                                <textarea
                                    value={editedPromptPackage.userPrompt}
                                    onChange={(e) => setEditedPromptPackage({
                                        ...editedPromptPackage,
                                        userPrompt: e.target.value
                                    })}
                                    className={styles.promptTextarea}
                                    rows={12}
                                />
                            ) : (
                                <pre className={styles.promptDisplay}>{selectedPrompt.userPrompt}</pre>
                            )}
                        </div>

                        {/* Recap Data Summary */}
                        <div className={styles.promptSection}>
                            <h5>Recap Data Summary:</h5>
                            <div className={styles.dataSummary}>
                                <p><strong>Total Picks:</strong> {selectedPrompt.recapData.totalPicks}</p>
                                <p><strong>Total Users:</strong> {selectedPrompt.recapData.totalUsers}</p>
                                <p><strong>Drama Highlights:</strong> {selectedPrompt.recapData.dramaHighlights}</p>
                                <p><strong>Margin Highlights:</strong> {selectedPrompt.recapData.marginHighlights}</p>
                            </div>
                        </div>

                        {/* LLM Response */}
                        {llmResponse && (
                            <div className={styles.promptSection}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h5>LLM Response:</h5>
                                    <button
                                        onClick={() => setEditingRecap(!editingRecap)}
                                        className={styles.actionButton}
                                        style={{ fontSize: '0.8em', padding: '4px 8px' }}
                                    >
                                        {editingRecap ? 'Cancel Edit' : 'Edit Recap'}
                                    </button>
                                </div>

                                {editingRecap ? (
                                    <div>
                                        <textarea
                                            value={editedRecap}
                                            onChange={(e) => setEditedRecap(e.target.value)}
                                            className={styles.promptTextarea}
                                            rows={12}
                                            placeholder="Edit the generated recap..."
                                        />
                                        <div style={{ marginTop: '10px' }}>
                                            <button
                                                onClick={saveRecapEdits}
                                                disabled={promptLoading}
                                                className={styles.actionButton}
                                                style={{ backgroundColor: '#28a745' }}
                                            >
                                                {promptLoading ? 'Saving...' : 'Save Recap Edits'}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.llmResponse}>
                                        {llmResponse}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Manual Prompt Generation */}
                <div className={styles.section}>
                    <h4>🔧 Manual Prompt Generation</h4>
                    <p>Generate a new prompt package for a specific week:</p>
                    <div className={styles.manualGeneration}>
                        <input
                            type="number"
                            placeholder="Week Number"
                            id="manualWeek"
                            className={styles.input}
                        />
                        <input
                            type="number"
                            placeholder="Season (e.g., 2025)"
                            id="manualSeason"
                            className={styles.input}
                        />
                        <button
                            onClick={() => {
                                const week = document.getElementById('manualWeek').value;
                                const season = document.getElementById('manualSeason').value;
                                if (week && season) {
                                    generateNewPrompt(parseInt(week), parseInt(season));
                                }
                            }}
                            disabled={promptLoading}
                            className={styles.actionButton}
                        >
                            {promptLoading ? 'Generating...' : 'Generate Prompt'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2>🤖 LLM Admin Panel</h2>
                <p>Manage AI-powered Discord content generation</p>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    ❌ {error}
                </div>
            )}

            {success && (
                <div className={styles.successMessage}>
                    ✅ {success}
                </div>
            )}

            <div className={styles.tabs}>
                <button
                    className={activeTab === 'settings' ? styles.activeTab : styles.tab}
                    onClick={() => setActiveTab('settings')}
                >
                    ⚙️ Settings
                </button>
                <button
                    className={activeTab === 'stats' ? styles.activeTab : styles.tab}
                    onClick={() => setActiveTab('stats')}
                >
                    📊 Statistics
                </button>
                <button
                    className={activeTab === 'history' ? styles.activeTab : styles.tab}
                    onClick={() => setActiveTab('history')}
                >
                    📋 History
                </button>
                <button
                    className={activeTab === 'test' ? styles.activeTab : styles.tab}
                    onClick={() => setActiveTab('test')}
                >
                    🧪 Test
                </button>
                <button
                    className={activeTab === 'groundRules' ? styles.activeTab : styles.tab}
                    onClick={() => setActiveTab('groundRules')}
                >
                    📋 Ground Rules
                </button>
                <button
                    className={activeTab === 'weeklyRecap' ? styles.activeTab : styles.tab}
                    onClick={() => setActiveTab('weeklyRecap')}
                >
                    📊 Weekly Recap
                </button>
            </div>

            <div className={styles.content}>
                {loading && <div className={styles.loading}>Loading...</div>}

                {!loading && activeTab === 'settings' && renderSettings()}
                {!loading && activeTab === 'stats' && renderUsageStats()}
                {!loading && activeTab === 'history' && renderUsageHistory()}
                {!loading && activeTab === 'test' && renderTestInterface()}
                {!loading && activeTab === 'groundRules' && renderGroundRules()}
                {!loading && activeTab === 'weeklyRecap' && renderWeeklyRecapTab()}
            </div>
        </div>
    );
};

export default LLMAdminPanel;
